<template>
  <div class="container-fluid">
    <TopSection
      :title="username | capitalize"
      :section="$t('members')"
      :back-section="members-list"
      :sub-section="$t('memberProfile')"
    >
      <template v-slot:actions>
        <b-button
          class="add-btn btn-outline-secondary"
          @click="$router.push({ name: 'members-list'})"
        >
          <span>{{ $t('cancel') | capitalize }}</span>
        </b-button>
        <b-button
          v-if="!isBasic"
          class="ml-2 add-btn btn-primary"
          @click="saveMember"
        >
          <spinner v-if="saving" />
          <span v-else>{{ $t('save') | capitalize }}</span>
        </b-button>
      </template>
    </TopSection>

    <div class="row pt-4">
      <div class="col-2">
        <Avatar
          v-if="username"
          :username="username"
          :size="132"
        />
      </div>

      <div class="col-4">
        <div class="column">
          <div>
            <span class="pt-2">{{ $t('member') | capitalize }}</span>
            <input
              v-model="username"
              class="form-control"
              disabled
            />
          </div>

          <div class="pt-4">
            <span class="pt-2">{{ $t('address') | capitalize }}</span>
            <input
              v-model="address"
              class="form-control"
              :disabled="readonly"
            >
          </div>
          <div class="pt-4">
            <span class="pt-2">{{ $t('nationality') | capitalize }}</span>
            <input
              v-model="nationality"
              class="form-control"
              disabled
            >
          </div>
          <div class="pt-4">
            <span class="pt-2">{{ $t('provider_id') | capitalize }}</span>
            <input
              v-model="provider_id"
              :maxlength=15
              class="form-control"
              :disabled="readonly"
            >
          </div>
          <div class="pt-4">
            <span class="pt-2">{{ $t('activation_date') | capitalize }}</span>
            <input
              v-model="activation_date"
              :maxlength=15
              class="form-control"
              :disabled="readonly"
            >
          </div>
        </div>
      </div>

      <div class="col-1" />

      <div class="col-1">
        <div class>
          <div>
            <span class="pt-2">{{ $t('status') | capitalize }}</span>

            <div class="row le">
              <Multiselect
                v-model="status"
                :options="statusOptions"
                :hide-selected="true"
                :show-labels="false"
              >
                <template v-slot:singleLabel="{ option }">
                  <div class="form_block2 societies space-between">
                    <div style="width:20px;">
                      <i :class="statusColor(option)" class="fa fa-circle"/>
                    </div>
                    <div class="option__desc">
                      <span class="option__title">{{ option | capitalize }}</span>
                    </div>
                  </div>
                </template>

                <template v-slot:option="{ option }">
                  <div class="form_block2 societies space-between">
                    <div style="width:20px;">
                      <i :class="statusColor(option)" class="fa fa-circle"/>
                    </div>
                    <div class="option__desc">
                      <span class="option__title">{{ option | capitalize }}</span>
                    </div>
                  </div>
                </template>
              </Multiselect>
            </div>
          </div>
        </div>
      </div>
    </div>
    <MandatesSelector :mandates="mandates"/>

    <!-- Save Member Confirmation -->
    <b-modal
      id="confirmSaveMember"
      ref="confirmSaveMember"
      :title="$t('save_member') | capitalize"
      hide-footer
    >
      <p>{{ $t("save_member_warning") }}</p>
      <div class="flex justify-end">
        <action-button
          variant="primary"
          size="big"
          @click="confirmMemberSaving()"
        >
          {{ $t("save_member") | capitalize }}
        </action-button>
      </div>
    </b-modal>

  </div>
</template>


<style lang="scss" scoped>
.vcenter {
  display: flex;
  align-items: center;
}

input {
  height: 48px;
  padding: 6px;
  color: black;
  border: 1px solid #c0c5cf;
  border-radius: 4px;
}

.member-name {
  height: 45px;
  width: 370px;
  color: #000;
  font-family: "CircularStd", sans-serif;
  font-size: 26px;
  font-weight: 500;
  line-height: 33px;
}

a.pointer {
  color: lightskyblue;

  &:hover {
    color: rgba(0, 0, 0, 0.54);
  }
}

.rights {
  height: 45px;
  width: 370px;
  color: #000;
  font-family: "CircularStd", sans-serif;
  font-size: 26px;
  font-weight: 500;
  line-height: 33px;
}

</style>



<style lang="scss">
@import "../../../assets/styles/libs.scss";

.h60p {
  height: 60px;
}

.column {
  display: flex;
  flex-direction: column;
}

.add-btn {
  height: 40px;
  width: 124.41px;
  font-family: "CircularStd", sans-serif;
  font-size: 16px;
  font-weight: bold;
  letter-spacing: -0.03px;
  text-align: center;
}
</style>

<script>
import { mapActions, mapGetters, mapState } from "vuex"
import { ApiCalls } from "@/assets/libs/services"

export default {
  name: "MemberDetail",
  data () {
    return {
      username: "",
      nationality: "",
      address: "",
      provider_id: "",
      activation_date: "",
      status: "",
      org_type: "",
      mandates: [],
      saving: false
    }
  },
  computed: {
    readonly () {
      return this.isBasic
    },
    member_screen_title () {
      return this.isBasic ? this.$t("company_profile") : this.$t("edit_org")
    },
    statusOptions () {
      let options = [
        this.statusTypes.ACTIVE,
        this.statusTypes.BLOCKED,
      ]
      if (this.statusTypes.PENDING === this.savedStatus) {
        options.push(this.statusTypes.PENDING)
      }
      return options
    },
    ...mapGetters("soundsys", [
      "isMlc",
      "isSisterSociety",
      "isSuperUser",
      "isBasic",
      "isAdvanced",
      "countries",
      "orgTypes",
      "statusTypes"
    ]),
    ...mapState("members", ["memberDetail", "orgs_types"]),
    ...mapState("soundsys", ["user"]),
  },
  methods: {
    actionClass (expected) {
      let _class = this.status !== expected ? "clear" : ""
      return this.isBasic ? _class : _class + " clickable"
    },
    activateMember () {
      this.saving = true
      if (
        !this.mandates.some(mandate => mandate.worldwide || mandate.domestic)
      ) {
        this.saving = false

        return Promise.reject({ error: "first you should add mandates" })
      }
      return ApiCalls.activateMember(this.id)
    },
    blockMember () {
      this.saving = true
      return ApiCalls.blockMember(this.id)
    },
    getMandateModeValue (mandate) {
      if (mandate.worldwide) {
        return "but"
      }
      if (mandate.domestic) {
        return "and"
      }
    },
    statusColor (state) {
      return state + "_state"
    },
    saveState () {
      return new Promise((resolve, reject) => {
        if (this.savedStatus === this.status) {
          return resolve()
        }
        if (this.status === this.statusTypes.ACTIVE) {
          this.activateMember()
            .then(() => {
              this.savedStatus = this.statusTypes.ACTIVE
              this.saving = false
              resolve()
            })
            .catch(error => {
              this.$toasted.global.toast(error.error)
              this.saving = false
              reject()
            })
        } else if (this.status === this.statusTypes.BLOCKED) {
          this.blockMember(this.id)
            .then(() => {
              this.savedStatus = this.statusTypes.BLOCKED
              this.saving = false
              resolve()
            })
            .catch(error => {
              this.$toasted.global.toast(error.error)
              this.saving = false
              reject()
            })
        }
      })
    },
    activate (state) {
      this.status = state
    },
    checkmandates (name, scope) {
      let mandate = this.mandates.filter(x => x.name === name)[0]
      let val = mandate[scope]

      if (val) {
        let otherscope = scope === "domestic" ? "worldwide" : "domestic"
        mandate[otherscope] = false
      }
    },

    saveMember () {
      this.$bvModal.show("confirmSaveMember")
    },
    confirmMemberSaving () {
      this.$bvModal.hide("confirmSaveMember")
      this.saving = true
      const mandates = this.mandates.map(mandate => {
        if (mandate.countries.length) {
          return {
            ...mandate,
            countries: mandate.countries.map(country => country.alpha_2)
          }
        }
        return { ...mandate, countries: [] }
      })
      const is_mandates = mandates.some(mandate => mandate.domestic || mandate.worldwide)

      if (this.status === this.statusTypes.ACTIVE && !is_mandates) {
        this.saving = false

        return this.$toasted.global.toast(this.$t("choose_mandates_before_activation"))
      }

      if (this.isMlc && !this.isBasic) {
        let data = {
          id: this.id,
          username: this.username,
          address: this.address,
          nationality: this.nationality,
          provider_id: this.provider_id,
          activation_date: this.activation_date,
          status: this.status,
          org_type: this.org_type,
          status_updated: this.savedStatus !== this.status,
          mandates: mandates
        }
        ApiCalls.save_member(data)
          .then(() => {
            this.saveState().then(() => {
              this.$toasted.global.toast(this.$t("member_saved"))
              this.$forceUpdate()
              this.saving = false
            }).catch(error => {
              this.saving = false
              this.handleError(error)
            })
          })
          .catch(error => {
            this.saving = false
            this.handleError(error)
          })
      }
    },
    handleError (error) {
      if (error.detail) {
        this.$toasted.global.toast(error.detail)
      }
      else if (Array.isArray(error)) {
        for (let i in error) {
          let msg = error[i]
          this.$toasted.global.toast(msg)
        }
      }
      else if (Array.isArray(error.errors)) {
        for (let i in error.errors) {
          let msg = error.errors[i]
          this.$toasted.global.toast(msg)
        }
      } else if (typeof error.errors === "object") {
        for (let k in error.errors) {
          let msg = error.errors[k]
          this.$toasted.global.toast(k + ": " + msg)
        }
      } else {
        this.$toasted.global.toast(error.errors)
      }
    },
    copyMandates () {
      if (!this.memberDetail.mandates) return []
      return this.memberDetail.mandates.map(mandate => ({
        name: mandate.name,
        domestic: mandate.domestic,
        worldwide: mandate.worldwide,
        countries: mandate.countries,
        has_agreement: mandate.has_agreement
      }))
    },
    mapData () {
      if (this.memberDetail !== undefined) {
        this.username = this.memberDetail.username
        this.address = this.memberDetail.address
        this.provider_id = this.memberDetail.provider_ids[this.user.society]
        this.activation_date = this.memberDetail.activation_dates[this.user.society]
        this.status = this.memberDetail.society_status
        this.savedStatus = this.memberDetail.society_status
        this.org_type = this.memberDetail.org_type
        this.mandates = this.copyMandates()
      } else {
        this.username = ""
        this.address = ""
        this.status = ""
        this.provider_id = ""
        this.activation_date = ""
        this.org_type = ""
        this.mandates = []
      }
    },
    getUserImage () {},
    ...mapActions("members", ["getMemberData"])
  },

  mounted () {
    this.getMemberData(this.id).then(() => this.mapData())
  },
  props: {
    id: [Number, String]
  }
}
</script>

